import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../lib/api.js";
import {
  deleteHousingExpense,
  listHousingExpenses,
} from "../../lib/api-modules/housing-expenses.js";
import {
  deleteHousingAssessment,
  listHousingAssessments,
} from "../../lib/api-modules/housing-assessments.js";
import {
  ASSESSMENT_TYPE_LABELS,
  HOUSING_EXPENSE_TYPE_LABELS,
  OWNERSHIP_TYPE_LABELS,
  type HousingAssessment,
  type HousingExpense,
  type HousingUnit,
} from "../../lib/api-modules/types.js";
import { aggregateDeductibility } from "../../lib/util/housing.js";
import { formatCentsCurrency } from "../../lib/util/money.js";
import { ConfirmButton } from "../confirm-button.js";
import { Toast, useToast } from "../toast.js";
import { NotesPanel } from "../notes-panel.js";
import { DecisionsPanel } from "../decisions-panel.js";
import { HousingExpenseForm } from "./HousingExpenseForm.js";
import { HousingAssessmentForm } from "./HousingAssessmentForm.js";
import { AmortizationTab } from "./AmortizationTab.js";
import { RefinanceTab } from "./RefinanceTab.js";

type TabKey =
  | "overview"
  | "expenses"
  | "assessments"
  | "amortization"
  | "refinance";

interface HousingUnitDrilldownProps {
  readonly unit: HousingUnit;
  readonly canWrite: boolean;
}

/**
 * Multi-tab per-unit drilldown — the first multi-tab pattern in the
 * Hearth SPA. Tab state lives locally; switching tabs doesn't change
 * the URL (deliberate — drilldown is ephemeral, not bookmarkable). If
 * a future session wants deep-linking we can lift state to URL params.
 */
export function HousingUnitDrilldown({
  unit,
  canWrite,
}: HousingUnitDrilldownProps) {
  const [activeTab, setActiveTab] = useState<TabKey>("overview");

  return (
    <div className="rounded-md border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
      <nav
        className="flex flex-wrap gap-1 border-b border-zinc-200 px-3 pt-2 dark:border-zinc-800"
        aria-label="Unit details"
      >
        <Tab
          label="Overview"
          active={activeTab === "overview"}
          onClick={() => setActiveTab("overview")}
        />
        <Tab
          label="Expenses"
          active={activeTab === "expenses"}
          onClick={() => setActiveTab("expenses")}
        />
        <Tab
          label="Assessments"
          active={activeTab === "assessments"}
          onClick={() => setActiveTab("assessments")}
        />
        <Tab
          label="Amortization"
          active={activeTab === "amortization"}
          onClick={() => setActiveTab("amortization")}
        />
        <Tab
          label="Refinance"
          active={activeTab === "refinance"}
          onClick={() => setActiveTab("refinance")}
        />
      </nav>

      <div className="p-4">
        {activeTab === "overview" && <OverviewTab unit={unit} />}
        {activeTab === "expenses" && (
          <ExpensesTab unit={unit} canWrite={canWrite} />
        )}
        {activeTab === "assessments" && (
          <AssessmentsTab unit={unit} canWrite={canWrite} />
        )}
        {activeTab === "amortization" && <AmortizationTab unit={unit} />}
        {activeTab === "refinance" && <RefinanceTab unit={unit} />}
      </div>
    </div>
  );
}

function Tab({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "border-b-2 border-brand-500 px-3 py-2 text-sm font-medium text-brand-600 dark:text-brand-400"
          : "border-b-2 border-transparent px-3 py-2 text-sm text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
      }
    >
      {label}
    </button>
  );
}

function OverviewTab({ unit }: { unit: HousingUnit }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
        <dt className="text-zinc-500">Ownership</dt>
        <dd>{OWNERSHIP_TYPE_LABELS[unit.ownership_type]}</dd>
        <dt className="text-zinc-500">Address</dt>
        <dd>
          {unit.address}
          {unit.unit_designation && `, ${unit.unit_designation}`}
        </dd>
        <dt className="text-zinc-500">City / state / zip</dt>
        <dd>
          {unit.city}, {unit.state} {unit.zip}
        </dd>
        <dt className="text-zinc-500">Primary residence</dt>
        <dd>{unit.is_primary_residence ? "Yes" : "No"}</dd>
        {unit.purchase_price_cents !== undefined && (
          <>
            <dt className="text-zinc-500">Purchased</dt>
            <dd>
              {unit.purchase_date} ·{" "}
              {formatCentsCurrency(unit.purchase_price_cents)}
            </dd>
          </>
        )}
        {unit.current_value_cents !== undefined && (
          <>
            <dt className="text-zinc-500">Current value</dt>
            <dd>
              {formatCentsCurrency(unit.current_value_cents)}
              {unit.current_value_as_of && (
                <span className="text-xs text-zinc-500">
                  {" "}
                  as of {unit.current_value_as_of}
                </span>
              )}
            </dd>
          </>
        )}
        {unit.shares_held !== undefined && (
          <>
            <dt className="text-zinc-500">Co-op shares</dt>
            <dd>
              {unit.shares_held} of {unit.total_shares_outstanding} (
              {unit.total_shares_outstanding
                ? ((unit.shares_held / unit.total_shares_outstanding) * 100).toFixed(2)
                : "—"}
              %)
            </dd>
          </>
        )}
        {unit.underlying_mortgage_share_cents !== undefined && (
          <>
            <dt className="text-zinc-500">Your underlying mortgage share</dt>
            <dd>{formatCentsCurrency(unit.underlying_mortgage_share_cents)}</dd>
          </>
        )}
        {unit.flip_tax_pct !== undefined && (
          <>
            <dt className="text-zinc-500">Flip tax</dt>
            <dd>
              {unit.flip_tax_pct}%
              {unit.flip_tax_basis && ` on ${unit.flip_tax_basis.replace("_", " ")}`}
            </dd>
          </>
        )}
        {unit.year_built !== undefined && (
          <>
            <dt className="text-zinc-500">Year built</dt>
            <dd>{unit.year_built}</dd>
          </>
        )}
        {unit.square_feet !== undefined && (
          <>
            <dt className="text-zinc-500">Square feet</dt>
            <dd>{unit.square_feet.toLocaleString()}</dd>
          </>
        )}
      </dl>

      <div className="flex flex-col gap-4">
        <NotesPanel entityType="housing_unit" entityId={unit.unit_id} />
        <DecisionsPanel entityType="housing_unit" entityId={unit.unit_id} />
      </div>
    </div>
  );
}

function ExpensesTab({
  unit,
  canWrite,
}: {
  unit: HousingUnit;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [year, setYear] = useState<number>(new Date().getUTCFullYear());

  const expensesQuery = useQuery({
    queryKey: ["housing-expenses", unit.unit_id, year],
    queryFn: () =>
      listHousingExpenses(unit.unit_id, {
        from: `${year}-01-01`,
        to: `${year}-12-31`,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (e: HousingExpense) =>
      deleteHousingExpense(unit.unit_id, e.payment_date, e.expense_id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["housing-expenses", unit.unit_id],
      });
      toast.show("Expense deleted");
    },
    onError: (err) =>
      toast.show(
        err instanceof ApiError
          ? `Couldn't delete: ${err.message}`
          : "Couldn't delete expense",
      ),
  });

  const expenses = expensesQuery.data?.housing_expenses ?? [];
  const rollup = aggregateDeductibility(expenses, year);

  return (
    <section>
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <label className="text-xs text-zinc-500">Tax year</label>
          <input
            type="number"
            className="w-24 rounded-md border border-zinc-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
          />
        </div>
        {canWrite && !showCreate && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-brand-600"
          >
            Add expense
          </button>
        )}
      </header>

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile label="Total paid" value={formatCentsCurrency(rollup.total_paid_cents)} />
        <Tile
          label="Mortgage interest (deductible)"
          value={formatCentsCurrency(rollup.mortgage_interest_total_cents)}
        />
        <Tile
          label="Property tax (deductible)"
          value={formatCentsCurrency(rollup.property_tax_total_cents)}
        />
        <Tile
          label="Non-deductible"
          value={formatCentsCurrency(rollup.non_deductible_total_cents)}
        />
      </div>

      {showCreate && (
        <div className="mb-4">
          <HousingExpenseForm
            mode="create"
            unitId={unit.unit_id}
            onClose={() => setShowCreate(false)}
          />
        </div>
      )}

      {expensesQuery.isLoading ? (
        <p className="text-sm text-zinc-500">Loading expenses…</p>
      ) : expenses.length === 0 ? (
        <p className="rounded-md border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500 dark:border-zinc-700">
          No expenses for {year} yet.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {expenses.map((e) => (
            <li
              key={e.expense_id}
              className="rounded-md border border-zinc-200 p-3 text-sm dark:border-zinc-800"
            >
              {editingId === e.expense_id ? (
                <HousingExpenseForm
                  mode="edit"
                  unitId={unit.unit_id}
                  expense={e}
                  onClose={() => setEditingId(null)}
                />
              ) : (
                <article className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium">
                      {HOUSING_EXPENSE_TYPE_LABELS[e.expense_type]}
                      <span className="ml-2 font-mono text-xs text-zinc-500">
                        {e.payment_date}
                      </span>
                    </div>
                    {e.vendor_or_payee && (
                      <div className="text-xs text-zinc-500">
                        {e.vendor_or_payee}
                        {e.period_covered && ` · ${e.period_covered}`}
                      </div>
                    )}
                    {(e.deductible_mortgage_interest_cents !== undefined ||
                      e.deductible_property_tax_cents !== undefined) && (
                      <div className="mt-1 text-xs text-zinc-500">
                        {e.deductible_mortgage_interest_cents !== undefined &&
                          ` interest ${formatCentsCurrency(e.deductible_mortgage_interest_cents)}`}
                        {e.deductible_property_tax_cents !== undefined &&
                          ` · prop. tax ${formatCentsCurrency(e.deductible_property_tax_cents)}`}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="font-mono text-sm">
                      {formatCentsCurrency(e.amount_cents)}
                    </span>
                    {canWrite && (
                      <>
                        <button
                          type="button"
                          onClick={() => setEditingId(e.expense_id)}
                          className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
                        >
                          Edit
                        </button>
                        <ConfirmButton
                          label="Delete"
                          pendingLabel="Deleting…"
                          size="xs"
                          onConfirm={() => deleteMutation.mutate(e)}
                          isPending={deleteMutation.isPending}
                        />
                      </>
                    )}
                  </div>
                </article>
              )}
            </li>
          ))}
        </ul>
      )}

      <Toast message={toast.message} />
    </section>
  );
}

function AssessmentsTab({
  unit,
  canWrite,
}: {
  unit: HousingUnit;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const assessmentsQuery = useQuery({
    queryKey: ["housing-assessments", unit.unit_id],
    queryFn: () => listHousingAssessments(unit.unit_id),
  });

  const deleteMutation = useMutation({
    mutationFn: (a: HousingAssessment) =>
      deleteHousingAssessment(unit.unit_id, a.assessment_id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["housing-assessments", unit.unit_id],
      });
      toast.show("Assessment deleted");
    },
    onError: (err) =>
      toast.show(
        err instanceof ApiError
          ? `Couldn't delete: ${err.message}`
          : "Couldn't delete assessment",
      ),
  });

  const assessments = assessmentsQuery.data?.housing_assessments ?? [];

  return (
    <section>
      <header className="mb-3 flex items-center justify-between">
        <div className="text-xs text-zinc-500">
          {assessments.length}{" "}
          {assessments.length === 1 ? "assessment" : "assessments"}
        </div>
        {canWrite && !showCreate && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-brand-600"
          >
            Add assessment
          </button>
        )}
      </header>

      {showCreate && (
        <div className="mb-4">
          <HousingAssessmentForm
            mode="create"
            unitId={unit.unit_id}
            onClose={() => setShowCreate(false)}
          />
        </div>
      )}

      {assessmentsQuery.isLoading ? (
        <p className="text-sm text-zinc-500">Loading assessments…</p>
      ) : assessments.length === 0 ? (
        <p className="rounded-md border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500 dark:border-zinc-700">
          No assessments yet.
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {assessments.map((a) => (
            <li
              key={a.assessment_id}
              className="rounded-md border border-zinc-200 p-3 text-sm dark:border-zinc-800"
            >
              {editingId === a.assessment_id ? (
                <HousingAssessmentForm
                  mode="edit"
                  unitId={unit.unit_id}
                  assessment={a}
                  onClose={() => setEditingId(null)}
                />
              ) : (
                <article>
                  <header className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="font-medium">
                        {ASSESSMENT_TYPE_LABELS[a.assessment_type]}
                        {a.assessment_type === "facade_law_11" && (
                          <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                            FISP cycle
                          </span>
                        )}
                        {a.is_capitalizable_to_basis && (
                          <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                            capital
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {a.start_date}
                        {a.end_date && ` → ${a.end_date}`} ·{" "}
                        {a.payment_schedule}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="font-mono text-sm">
                        {formatCentsCurrency(a.total_amount_cents)}
                      </span>
                      {canWrite && (
                        <>
                          <button
                            type="button"
                            onClick={() => setEditingId(a.assessment_id)}
                            className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
                          >
                            Edit
                          </button>
                          <ConfirmButton
                            label="Delete"
                            pendingLabel="Deleting…"
                            size="xs"
                            onConfirm={() => deleteMutation.mutate(a)}
                            isPending={deleteMutation.isPending}
                          />
                        </>
                      )}
                    </div>
                  </header>
                  {a.per_share_cents !== undefined &&
                    unit.shares_held !== undefined && (
                      <p className="mt-2 text-xs text-zinc-500">
                        Your share: {unit.shares_held} ×{" "}
                        {formatCentsCurrency(a.per_share_cents)} ={" "}
                        <strong className="font-mono">
                          {formatCentsCurrency(
                            a.per_share_cents * unit.shares_held,
                          )}
                        </strong>
                      </p>
                    )}
                  {a.notes && (
                    <p className="mt-2 whitespace-pre-wrap text-xs text-zinc-700 dark:text-zinc-300">
                      {a.notes}
                    </p>
                  )}
                  <div className="mt-3 border-t border-zinc-200 pt-3 dark:border-zinc-800">
                    <DecisionsPanel
                      entityType="housing_assessment"
                      entityId={a.assessment_id}
                    />
                  </div>
                </article>
              )}
            </li>
          ))}
        </ul>
      )}

      <Toast message={toast.message} />
    </section>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-zinc-200 p-3 dark:border-zinc-800">
      <div className="text-xs text-zinc-500">{label}</div>
      <div className="mt-1 font-mono text-sm">{value}</div>
    </div>
  );
}
